import { IconArrowsLeftRight, IconInfoCircleFilled } from '@tabler/icons-react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';

import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Popover } from 'src/design/molecules/Popover';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DivisionDto,
  IntegrationStatusDto,
  JobDto,
  JobSummaryDto,
} from 'src/api/FiretrolApiClient';
import {
  useCtcLinkedJobNumbers,
  useCtcList,
} from 'src/api/queries/ctc/useCtcList';
import { useCtcCurrentMonthStatus } from 'src/api/queries/ctc/useCtcCurrentMonthStatus';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { logger, formatDateTime } from 'src/utils';
import { Currency } from 'src/design/atoms/Currency';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { DivisionSelector } from './components/DivisionSelector';
import { MilestoneCell } from './components/MilestoneCell';

const CTC_FILTER_KEY = '@firetrol_ctc_filters:06112025';

const EMPTY_JOBS: JobSummaryDto[] = [];
const EMPTY_LINKED: string[] = [];

function readSavedDivisions(): DivisionDto[] {
  try {
    const saved = localStorage.getItem(CTC_FILTER_KEY);
    if (!saved) return [];
    return JSON.parse(saved).divisions ?? [];
  } catch (err) {
    logger.error('Error loading divisions', { err });
    return [];
  }
}

export const CTC: React.FC = () => {
  const navigate = useNavigate();
  const [divisions, setDivisions] =
    React.useState<DivisionDto[]>(readSavedDivisions);

  const divisionNames = React.useMemo(
    () => divisions.map((d) => d.name),
    [divisions],
  );

  const listQuery = useCtcList(divisionNames);
  const linkedJobsQuery = useCtcLinkedJobNumbers();
  const statusQuery = useCtcCurrentMonthStatus();

  const jobs = listQuery.data?.jobs ?? EMPTY_JOBS;
  const linkedJobs = linkedJobsQuery.data ?? EMPTY_LINKED;
  const status = statusQuery.data ?? null;

  const linkedDetails = React.useMemo<Record<string, JobDto>>(
    () =>
      (listQuery.data?.linkedJobs ?? []).reduce<Record<string, JobDto>>(
        (map, obj) => {
          map[obj.jobNumber.trim()] = obj;
          return map;
        },
        {},
      ),
    [listQuery.data],
  );

  const updateDivisions = (divs: DivisionDto[]) => {
    localStorage.setItem(CTC_FILTER_KEY, JSON.stringify({ divisions: divs }));
    setDivisions(divs);
  };

  // Calculate totals for footer display
  const totals = React.useMemo(() => {
    return {
      ContractAmt: jobs.reduce((sum, job) => sum + (job.contractAmt || 0), 0),
      EstCostPeriodJTD: jobs.reduce(
        (sum, job) => sum + (job.estCostPeriodJtd || 0),
        0,
      ),
      PeriodJTDCosts: jobs.reduce(
        (sum, job) => sum + (job.periodJtdCosts || 0),
        0,
      ),
      CostAtComp: jobs.reduce((sum, job) => sum + (job.costAtComp || 0), 0),
      RevEarned_PeriodJTD: jobs.reduce(
        (sum, job) => sum + (job.revEarnedPeriodJtd || 0),
        0,
      ),
      RevEarnedCurrMonth: jobs.reduce(
        (sum, job) => sum + (job.revEarnedCurrMonth || 0),
        0,
      ),
      Current_Cost_Month: jobs.reduce(
        (sum, job) => sum + (job.currentCostMonth || 0),
        0,
      ),
      MarginCurrentMonth: jobs.reduce(
        (sum, job) => sum + (job.marginCurrentMonth || 0),
        0,
      ),
    };
  }, [jobs]);

  const columns = React.useMemo<MRT_ColumnDef<JobSummaryDto>[]>(
    () => [
      {
        id: 'linked',
        header: '',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 40,
        enableResizing: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const jn = row.original.wsJobNumber;
          const isLinked = linkedJobs.find((l) => l === jn.trim());

          return (
            <Group gap={2} wrap="nowrap">
              {isLinked ? (
                <IconArrowsLeftRight size={18} style={{ color: '#058100' }} />
              ) : null}
              <Popover width={350} position="right" withArrow shadow="md">
                <Popover.Target>
                  <BasicActionIcon size="xs" variant="subtle" color="gray">
                    <IconInfoCircleFilled size={18} />
                  </BasicActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="xs">
                    <Title order={5}>
                      {row.original.wsJobNumber} - {row.original.wsJobName}
                    </Title>
                    <Text>Customer: {row.original.custNmbr}</Text>
                    <Text>Division: {row.original.divisions}</Text>
                    <Text>Salesperson: {row.original.salesPersonName}</Text>
                    <Text>
                      Certified Payroll:{' '}
                      {row.original.certifiedPayroll === 0 ? 'No' : 'Yes'}
                    </Text>
                    <Text>OCIP: {row.original.ocip}</Text>
                    <Text>
                      Liquidated Damages: {row.original.liquidatedDamages}
                    </Text>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            </Group>
          );
        },
      },
      {
        id: 'job',
        accessorFn: (row) => `${row.wsJobNumber} - ${row.wsJobName}`,
        header: 'Job Number - Job Name',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 250,
        Cell: ({ row, cell }) => (
          <LinkButton
            size="compact-xs"
            to={`/ctc/view/${row.original.wsJobNumber}`}
          >
            {cell.getValue<string>()}
          </LinkButton>
        ),
      },
      {
        id: 'milestone',
        accessorFn: (row) => {
          const jobNumber = row.wsJobNumber || '';
          const target = linkedDetails[jobNumber.trim()];
          return target?.milestone?.title;
        },
        header: 'Milestone',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ row, cell }) => (
          <MilestoneCell
            milestoneTitle={cell.getValue<string>()}
            jobNumber={row.original.wsJobNumber}
            linkedDetails={linkedDetails}
          />
        ),
      },
      {
        accessorKey: 'divisions',
        header: 'Division',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Footer: () => <Text c="black">Totals</Text>,
      },
      {
        accessorKey: 'contractAmt',
        header: 'Contract Amt.',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,

        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.ContractAmt < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.ContractAmt} />
          </Text>
        ),
      },
      {
        accessorKey: 'estCostPeriodJtd',
        header: 'Est Costs',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.EstCostPeriodJTD < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.EstCostPeriodJTD} />
          </Text>
        ),
      },
      {
        accessorKey: 'periodJtdCosts',
        header: 'JTD Costs',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.PeriodJTDCosts < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.PeriodJTDCosts} />
          </Text>
        ),
      },
      {
        accessorKey: 'costAtComp',
        header: 'C @ C',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text size="xs" ta="left" c={totals.CostAtComp < 0 ? 'red' : 'black'}>
            <Currency value={totals.CostAtComp} />
          </Text>
        ),
      },
      {
        accessorKey: 'pctCompleted',
        header: '% Comp',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              {Math.round(value)}%
            </Text>
          );
        },
      },
      {
        accessorKey: 'gmPrcnt',
        header: 'GM %',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              {Math.round(value)}%
            </Text>
          );
        },
      },
      {
        accessorKey: 'revEarnedPeriodJtd',
        header: 'Rev Earned',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.RevEarned_PeriodJTD < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.RevEarned_PeriodJTD} />
          </Text>
        ),
      },
      {
        accessorKey: 'revEarnedCurrMonth',
        header: 'Rev Earned',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#a3def8',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.RevEarnedCurrMonth < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.RevEarnedCurrMonth} />
          </Text>
        ),
      },
      {
        accessorKey: 'currentCostMonth',
        header: 'Cost',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#a3def8',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.Current_Cost_Month < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.Current_Cost_Month} />
          </Text>
        ),
      },
      {
        accessorKey: 'marginCurrentMonth',
        header: 'Margin',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#a3def8',
          },
        },
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>() || 0;
          return (
            <Text size="xs" ta="left" c={value < 0 ? 'red' : 'black'}>
              <Currency value={value} />
            </Text>
          );
        },
        Footer: () => (
          <Text
            size="xs"
            ta="left"
            c={totals.MarginCurrentMonth < 0 ? 'red' : 'black'}
          >
            <Currency value={totals.MarginCurrentMonth} />
          </Text>
        ),
      },
    ],
    [linkedJobs, linkedDetails, totals],
  );

  const tableState = useTableState({
    identifier: 'ctc-table',
    columns: columns,
    defaultSorting: [{ id: 'job', desc: false }],
  });

  const tableExports = useTableExports<JobSummaryDto>('ctc-report', (data) => {
    return data.map((item) => {
      const jobNumber = item.wsJobNumber || '';
      const linkedJob = linkedDetails[jobNumber.trim()];
      return {
        ['Job Number']: item.wsJobNumber,
        ['Job Name']: item.wsJobName,
        ['Milestone']: linkedJob?.milestone?.title || '',
        ['Division']: item.divisions || '',
        ['Contract Amt.']: item.contractAmt || 0,
        ['Est Costs']: item.estCostPeriodJtd || 0,
        ['JTD Costs']: item.periodJtdCosts || 0,
        ['C @ C']: item.costAtComp || 0,
        ['% Comp']: Math.round(item.pctCompleted || 0),
        ['GM %']: Math.round(item.gmPrcnt || 0),
        ['Rev Earned (Overall)']: item.revEarnedPeriodJtd || 0,
        ['Rev Earned (Current Month)']: item.revEarnedCurrMonth || 0,
        ['Cost (Current Month)']: item.currentCostMonth || 0,
        ['Margin (Current Month)']: item.marginCurrentMonth || 0,
      };
    });
  });

  const { table } = useTable({
    ...tableState,
    size: 'compact',
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      enableColumnResizing: false,
      enableColumnDragging: false,
      columns: columns,
      data: jobs,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableTableFooter: true,
      renderTopToolbarCustomActions: () => (
        <Box w={{ base: '100%', xl: '600px' }}>
          <DivisionSelector
            onChange={updateDivisions}
            value={divisions}
            hideLabel={true}
          />
        </Box>
      ),
    },
    getRowId: (row) => row.wsJobNumber,
    totalCount: jobs.length,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isError: listQuery.isError,
    ...tableExports,
  });

  const getStatusLabel = (s: IntegrationStatusDto) => {
    if (s.intgStatusId === 'LOCK') {
      return 'Locked - Pending Update';
    }
    if (s.intgStatusId === 'READY') {
      return 'Locked - Ready for CTC Entry';
    }
    if (s.intgStatusId === 'LIVE') {
      return 'Live';
    }
    return s.intgStatusId;
  };

  const renderStatus = () => (
    <Group gap="xs">
      {status?.lastUpdateDate ? (
        <Text style={{ marginLeft: 'auto' }}>
          Last Updated:{' '}
          {formatDateTime(status?.lastUpdateDate, 'MM/DD/YYYY @ h:mm a')}
        </Text>
      ) : (
        <Text>Last Updated: --/--/---- @ --:-- --</Text>
      )}

      <Text fw={600} c="black">
        {status ? getStatusLabel(status) : 'Loading...'}
      </Text>
    </Group>
  );

  const renderLegend = () => (
    <Group gap="xs" align="center">
      <Box style={{ backgroundColor: '#64ffc0', width: 18, height: 18 }} />
      <Text size="sm">Overall</Text>
      <Box style={{ backgroundColor: '#a3def8', width: 18, height: 18 }} />
      <Text size="sm">Current Month</Text>
    </Group>
  );

  return (
    <>
      <Header
        title="CTC"
        breadcrumbProps={{
          items: [{ text: 'CTC' }],
        }}
        helpText="Cost to Complete (CTC) management and analysis"
      />

      <Stack>
        <Group justify="space-between">
          {renderLegend()}
          {renderStatus()}
        </Group>

        {table}
      </Stack>
    </>
  );
};
